import { ApiError } from "./error";

import type { Endpoint, Input, Output } from "./codegen-rest";

export type SendRestOptions<E extends Endpoint> = {
  endpoint: E;
  signal?: AbortSignal;
} & Input<E>;

type ApiSuccess<T> = {
  success: true;
  data: T;
};

type ApiFailure = {
  success: false;
  data: {
    code: number;
    message: string;
  };
};

export type RestApiClientResponse<E extends Endpoint> = ApiSuccess<Output<E>> | ApiFailure;
export type RestApiClientSuccessResponse<E extends Endpoint> = ApiSuccess<Output<E>>;
export type RestApiResponse<E extends Endpoint> = ApiSuccess<Output<E>>["data"];

class RestApiClient {
  private static BASE_URL = window.location.origin + "/api/v1";
  private lastUnauthorizedEvent = 0;
  private readonly UNAUTHORIZED_THROTTLE = 3000;

  private dispatchUnauthorizedEvent() {
    const now = Date.now();
    if (now - this.lastUnauthorizedEvent >= this.UNAUTHORIZED_THROTTLE) {
      window.dispatchEvent(new Event("api:unauthorized"));
      this.lastUnauthorizedEvent = now;
    }
  }

  async send<E extends Endpoint>({
    endpoint,
    params,
    query,
    body,
    signal,
  }: SendRestOptions<E>): Promise<Output<E>> {
    const [method, path] = endpoint.split(" ");

    if (!method || !path) {
      throw new Error(`Invalid endpoint: ${endpoint}`);
    }

    const processedPath = this.setParams(path, params);
    const url = new URL(processedPath, RestApiClient.BASE_URL);

    this.setQuery(url, query);

    const request = new Request(url, {
      method,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });

    try {
      const response = await fetch(request);
      if (response.status === 401) {
        this.dispatchUnauthorizedEvent();
      }

      const parsedResponse = await response.json().catch(() => null);

      if (!response.ok) {
        const message =
          parsedResponse &&
          typeof parsedResponse === "object" &&
          "detail" in parsedResponse &&
          typeof (parsedResponse as { detail?: unknown }).detail === "string"
            ? (parsedResponse as { detail: string }).detail
            : response.statusText;
        throw new ApiError(response.status, message);
      }

      if (
        parsedResponse &&
        typeof parsedResponse === "object" &&
        Object.hasOwn(parsedResponse, "cloudflare_error")
      ) {
        throw new ApiError(response.status, response.statusText);
      }

      if (
        !parsedResponse ||
        typeof parsedResponse !== "object" ||
        !Object.hasOwn(parsedResponse, "success") ||
        !Object.hasOwn(parsedResponse, "data")
      ) {
        const message =
          typeof (parsedResponse as { detail?: unknown })?.detail === "string"
            ? (parsedResponse as { detail: string }).detail
            : response.statusText;
        throw new ApiError(response.status, message);
      }

      const { success, data } = parsedResponse as RestApiClientResponse<E>;

      if (!success) {
        if (data.code === 401 || data.code === -32001) {
          this.dispatchUnauthorizedEvent();
        }
        throw new ApiError(data.code, data.message);
      }

      return data;
    } catch (error) {
      if (error instanceof ApiError) throw error;

      throw new Error(String(error), { cause: error });
    }
  }

  private setParams(path: string, params?: Record<string, unknown>): string {
    let result = path;
    Object.entries(params ?? {}).forEach(([key, value]) => {
      result = result.replace(new RegExp(`\\{${key}\\}`, "g"), encodeURIComponent(String(value)));
    });
    return result;
  }

  private setQuery(url: URL, query?: Record<string, unknown>) {
    Object.entries(query ?? {}).forEach(([key, value]) => {
      if (value === undefined) {
        url.searchParams.delete(key);
      } else if (Array.isArray(value)) {
        url.searchParams.delete(key);
        value.forEach((item) => url.searchParams.append(key, String(item)));
      } else {
        url.searchParams.set(key, String(value));
      }
    });
  }
}

export const restApiClient = new RestApiClient();
